import { useState } from 'react';

import { useIntl } from 'react-intl-next';
import { useResource } from 'react-resource-router';

import { useDefaultCatch } from '@atlassian/eoc-catch';
import { errorToastProps } from '@atlassian/eoc-common';
import { useFlags } from '@atlassian/eoc-global-context';

import commonMessages from '../../common/messages';
import { type SavedSearch, type SaveSearchRequest } from '../../common/types';

import { createSavedSearchEndpoint, deleteSearchEndpoint } from './endpoints';
import { savedSearchesResource } from './resources';

export const useSavedSearchService = () => {
	const [isCreateLoading, setIsCreateLoading] = useState(false);
	const [isDeleteLoading, setIsDeleteLoading] = useState(false);
	const { showFlag } = useFlags();
	const defaultCatch = useDefaultCatch();
	const intlApi = useIntl();
	const { update } = useResource(savedSearchesResource);

	const createSavedSearch = (data: SaveSearchRequest): Promise<SavedSearch> => {
		setIsCreateLoading(true);

		return createSavedSearchEndpoint(data)
			.then((savedSearch) => {
				update((savedSearches) => ({
					...savedSearches,
					createdByMe: [...savedSearches.createdByMe, savedSearch],
				}));

				return savedSearch;
			})
			.catch((error) => {
				defaultCatch(
					{
						id: 'useSavedSearchService-createSavedSearch',
						httpErrorHandler: (err) =>
							showFlag({
								...errorToastProps(),
								description: err.message,
								title: intlApi.formatMessage(commonMessages.errorTitle),
							}),
					},
					error,
				);

				throw error;
			})
			.finally(() => {
				setIsCreateLoading(false);
			});
	};

	const deleteSavedSearch = (idToDelete: string) => {
		setIsDeleteLoading(true);

		return deleteSearchEndpoint(idToDelete)
			.then(() => {
				update((savedSearches) => ({
					...savedSearches,
					createdByMe: savedSearches.createdByMe.filter((search) => search.id !== idToDelete),
					sharedWithMe: savedSearches.sharedWithMe.filter((search) => search.id !== idToDelete),
				}));
			})
			.catch((error) => {
				defaultCatch(
					{
						id: 'useSavedSearchService-handleDeleteModalSubmit',
						httpErrorHandler: (err) =>
							showFlag({
								...errorToastProps(),
								description: err.message,
								title: intlApi.formatMessage(commonMessages.errorTitle),
							}),
					},
					error,
				);

				throw error;
			})
			.finally(() => {
				setIsDeleteLoading(false);
			});
	};

	return {
		createSavedSearch,
		isCreateLoading,
		isDeleteLoading,
		deleteSavedSearch,
	};
};
